import { useQueryState, UseQueryStateOptions, UseQueryStateReturn } from "nuqs";
import { useEffect } from "react";
import { usePersistedState } from "./use-persisted-state";

//source https://github.com/47ng/nuqs/discussions/832

export function useQueryStateWithLocalStorage<T>(
  queryKey: string,
  localStorageKey: string,
  options: UseQueryStateOptions<T> & {
    defaultValue: T;
  },
  equalityCheck: (a: T | null, b: T | null) => boolean = (a, b) => {
    return a === b;
  },
): UseQueryStateReturn<NonNullable<ReturnType<typeof options.parse>>, typeof options.defaultValue> {
  const { clearOnDefault = true, defaultValue } = options;

  // queryState is never null, it defaults to the defaultValue
  const [queryState, setQueryState] = useQueryState<T>(queryKey, options);

  // localStorageState defaults to null
  const [localStorageState, setLocalStorageState] = usePersistedState<ReturnType<UseQueryStateOptions<T>["parse"]>>(
    localStorageKey,
    queryState,
  );
  useEffect(() => {
    // If queryState is the same as localStorageState, do nothing
    if (equalityCheck(queryState, localStorageState)) return;

    // If queryState is not null and localStorageState is, set localStorageState to queryState
    if (queryState !== null && localStorageState === null) {
      setLocalStorageState(queryState);
      return;
    }

    // If queryState is null and localStorageState is not, set queryState to localStorageState
    if (
      (queryState === null || (clearOnDefault && equalityCheck(queryState, defaultValue))) &&
      localStorageState !== null &&
      localStorageState !== undefined
    ) {
      setQueryState(localStorageState);
      return;
    }

    // If queryState and localStorageState are different, set localStorageState to queryState, so queryState is the source of truth
    if (
      queryState !== null &&
      localStorageState !== null &&
      localStorageState !== undefined &&
      !equalityCheck(queryState, localStorageState)
    ) {
      setLocalStorageState(queryState);
      return;
    }
  }, [queryState, localStorageState, setLocalStorageState, setQueryState, defaultValue, clearOnDefault]);

  return [queryState, setQueryState];
}
